import { InjectionToken } from '@angular/core';
import type { CustomSpawnBracketIndex, CustomSpawnBracketRecord } from '@animus/type/domain';
import type { Observable } from 'rxjs';

export interface CustomSpawnBracketPersistence {
    list(): Promise<CustomSpawnBracketIndex>;
    /** Live query of the bracket index - re-emits whenever a bracket is saved, deleted, or replicated in. */
    list$(): Observable<CustomSpawnBracketIndex>;
    load(id: string): Promise<CustomSpawnBracketRecord | null>;
    /**
     * Live query for a single bracket - emits `null` until the doc exists locally (e.g. before a WebRTC-joined
     * peer has received it via replication), then re-emits on every local or replicated change.
     */
    observe(id: string): Observable<CustomSpawnBracketRecord | null>;
    save(bracket: CustomSpawnBracketRecord): Promise<void>;
    /** Adds a durable game-reference relation so the record can replicate alongside that game without copying it. */
    linkToGame(bracketId: string, gameId: string): Promise<void>;
    delete(id: string): Promise<void>;
}

/** JSON-safe simulator state stored separately from the authored bracket. */
export interface CustomSpawnSimulatorSessionRecord {
    bracketId: string;
    sessionId: string;
    state: Record<string, unknown>;
    updatedAt: string;
}

/** Durable boundary for a shareable custom-spawn simulator session. */
export interface CustomSpawnSimulatorSessionPersistence {
    load(sessionId: string): Promise<CustomSpawnSimulatorSessionRecord | null>;
    observe(sessionId: string): Observable<CustomSpawnSimulatorSessionRecord | null>;
    save(record: CustomSpawnSimulatorSessionRecord): Promise<void>;
}

export const CUSTOM_SPAWN_SIMULATOR_SESSION_PERSISTENCE =
    new InjectionToken<CustomSpawnSimulatorSessionPersistence>('CUSTOM_SPAWN_SIMULATOR_SESSION_PERSISTENCE');

export const CUSTOM_SPAWN_BRACKET_PERSISTENCE = new InjectionToken<CustomSpawnBracketPersistence>(
    'CUSTOM_SPAWN_BRACKET_PERSISTENCE'
);
